/**
 * @file Frontend API client — adminDatasetExportService.
 */

import axiosClient from "../api/axiosClient";
import { API_URLS } from "../constants/apiUrls";

function parseFilename(contentDisposition: string | undefined, fallback: string): string {
  if (!contentDisposition) return fallback;
  const match = /filename="?([^";\n]+)"?/i.exec(contentDisposition);
  return match?.[1]?.trim() || fallback;
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

async function readBlobErrorMessage(blob: Blob): Promise<string> {
  try {
    const text = await blob.text();
    const parsed = JSON.parse(text);
    return parsed?.message || "Failed to download dataset.";
  } catch {
    return "Failed to download dataset.";
  }
}

/** Exported function — download Admin Material Dataset. */
export async function downloadAdminMaterialDataset(materialId: number): Promise<{
  filename: string;
  rowCount: number | null;
}> {
  const fallbackName = `ICoMEC_Admin_Dataset_${materialId}.xlsx`;

  const response = await axiosClient.get(API_URLS.ADMIN.DATASETS_DOWNLOAD(materialId), {
    responseType: "blob",
    timeout: 5 * 60 * 1000,
  });

  const contentType = String(response.headers["content-type"] || "");
  if (contentType.includes("application/json")) {
    const message = await readBlobErrorMessage(response.data as Blob);
    throw new Error(message);
  }

  const filename = parseFilename(response.headers["content-disposition"], fallbackName);
  const rowCountHeader = response.headers["x-export-row-count"];
  const rowCount =
    rowCountHeader != null && rowCountHeader !== "" ? Number(rowCountHeader) : null;

  triggerBlobDownload(response.data as Blob, filename);

  return {
    filename,
    rowCount: Number.isFinite(rowCount) ? rowCount : null,
  };
}
